import React, { useRef, useEffect } from 'react';

/**
 * Composant de saisie OTP à 6 chiffres moderne et fluide
 * Supporte : saisie automatique, retour arrière, touches fléchées, copier-coller (Ctrl+V)
 */
const OtpInput = ({ value = '', onChange, onComplete, disabled = false, autoFocus = true }) => {
  const inputRefs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, e) => {
    const rawVal = e.target.value;
    // Ne garder que le dernier chiffre tapé
    const cleanDigit = rawVal.replace(/\D/g, '').slice(-1);

    const newDigits = [...digits];
    newDigits[index] = cleanDigit;
    const newCode = newDigits.join('');
    onChange(newCode);

    if (cleanDigit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.length === 6 && onComplete) {
      onComplete(newCode);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        onChange(newDigits.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedText) return;

    onChange(pastedText);

    const nextFocusIndex = Math.min(pastedText.length, 5);
    inputRefs.current[nextFocusIndex]?.focus();

    if (pastedText.length === 6 && onComplete) {
      onComplete(pastedText);
    }
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center gap-2 my-3"
      onPaste={handlePaste}
      style={{ userSelect: 'none' }}
    >
      {Array.from({ length: 6 }).map((_, i) => {
        const isFilled = Boolean(digits[i]);
        return (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digits[i] || ''}
            disabled={disabled}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            style={{
              width: '46px',
              height: '56px',
              textAlign: 'center',
              fontSize: '22px',
              fontWeight: 700,
              fontFamily: 'SF Pro Display, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace',
              color: '#0f172a',
              backgroundColor: disabled ? '#f1f5f9' : isFilled ? '#f8fafc' : '#ffffff',
              border: isFilled ? '2px solid #009688' : '1.5px solid #cbd5e1',
              borderRadius: '12px',
              outline: 'none',
              boxShadow: isFilled ? '0 2px 6px rgba(0, 150, 136, 0.12)' : 'none',
              transition: 'all 0.15s ease-in-out',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#009688';
              e.target.style.boxShadow = '0 0 0 3px rgba(0, 150, 136, 0.18)';
              e.target.select();
            }}
            onBlur={(e) => {
              const hasVal = Boolean(digits[i]);
              e.target.style.borderColor = hasVal ? '#009688' : '#cbd5e1';
              e.target.style.boxShadow = hasVal ? '0 2px 6px rgba(0, 150, 136, 0.12)' : 'none';
            }}
          />
        );
      })}
    </div>
  );
};

export default OtpInput;
